import { CSSProperties } from "react";
import { isWhitelabel } from "./Constants";

// The hero banners on /flight-booking, /hotel-booking and /travel-insurance.
// A whitelabel admin can override each one on /branding-setup (company
// .service_banners, exposed to the admin and its agents as
// whitelabel_branding.service_banners); every other account, and any value
// the admin left blank, gets the Wanderlinx default below.
export type ServiceBannerKey = "flight" | "hotel" | "insurance";

export interface ServiceBanner {
  cover: string;
  headline: string;
  subheadline: string;
  font_color: string;
}

const PLANE_BACK = "https://cdn.mywanderlinx.com/assets/images/Planeback.jpg";

export const SERVICE_BANNER_LABELS: Record<ServiceBannerKey, string> = {
  flight: "Flight Booking Banner",
  hotel: "Hotel Booking Banner",
  insurance: "Travel Insurance Banner",
};

export const DEFAULT_SERVICE_BANNERS: Record<ServiceBannerKey, ServiceBanner> = {
  flight: {
    cover: PLANE_BACK,
    headline: "Navigating the Skies",
    subheadline:
      "Unleash Seamless Flight Booking with Cutting-Edge Search Technology.",
    font_color: "#ffffff",
  },
  hotel: {
    cover: PLANE_BACK,
    headline: "Enjoy Your Dream Vacation",
    subheadline:
      "More than 360,000 hotel, hostels, apartments and guest houses around the world.",
    font_color: "#ffffff",
  },
  insurance: {
    cover: PLANE_BACK,
    headline: "Travel Smarter, Travel Safer",
    subheadline:
      "Book faster, manage easier, and experience stress-free travel with Wanderlinx.",
    font_color: "#ffffff",
  },
};

/** The admin's saved overrides for one service (only the fields it set). */
export const savedServiceBanner = (
  currentUser: any,
  service: ServiceBannerKey
): Partial<ServiceBanner> =>
  (isWhitelabel(currentUser) &&
    currentUser?.whitelabel_branding?.service_banners?.[service]) ||
  {};

/** The banner to render: the admin's overrides on top of the defaults. */
export const getServiceBanner = (
  currentUser: any,
  service: ServiceBannerKey
): ServiceBanner => {
  const defaults = { ...DEFAULT_SERVICE_BANNERS[service] };

  // The stock insurance line names Wanderlinx — not for a whitelabel brand.
  if (service === "insurance" && isWhitelabel(currentUser)) {
    const brand = currentUser?.whitelabel_branding?.brand_name;
    defaults.subheadline = brand
      ? defaults.subheadline.replace("Wanderlinx", brand)
      : defaults.subheadline.replace(" with Wanderlinx", "");
  }

  return { ...defaults, ...savedServiceBanner(currentUser, service) };
};

/**
 * Style for the banner's <h2>/<h3>. index.css forces every heading to
 * `color: var(--vz-heading-color) !important`, which beats an inline
 * `color`, so set that variable on the heading instead.
 */
export const bannerHeadingStyle = (banner: ServiceBanner): CSSProperties =>
  ({ "--vz-heading-color": banner.font_color } as CSSProperties);
